import { api, BACKEND_URL, isLoggedIn } from '@/lib/auth';

// VAPID keys are base64url; PushManager wants a raw Uint8Array.
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

export const pushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// Called once the owner is logged in (Dashboard mount). Safe to call
// repeatedly — subscribing again with the same endpoint just refreshes it.
export const ensurePushSubscription = async () => {
  if (!pushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';

  try {
    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      if (Notification.permission !== 'granted') {
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return 'denied';
      }

      const { data } = await api.get('/push/vapid-public-key');
      if (!data.publicKey) return 'unavailable';

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });
    }

    await api.post('/push/subscribe', subscription.toJSON());
    return 'subscribed';
  } catch (err) {
    console.error('Push subscription failed:', err);
    return 'error';
  }
};

// Stops this browser receiving call/message alerts for the account that just
// logged out. Must NOT depend on being logged in (the session is already gone
// on a later page load), so the server endpoint is keyed by the push endpoint
// itself and called without auth.
export const clearPushSubscription = async () => {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return;
    try {
      await fetch(`${BACKEND_URL}/api/push/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: subscription.endpoint }),
        keepalive: true,
      });
    } catch {
      // offline — the browser-side unsubscribe below still stops delivery
    }
    await subscription.unsubscribe();
  } catch {}
};

// Called on app start: if nobody is logged in, this browser must not still be
// subscribed (covers logouts that happened before this fix, expired sessions…).
export const dropPushIfLoggedOut = () => {
  if (!isLoggedIn()) clearPushSubscription();
};
